import React from "react";
import { Box, Card, CardContent, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LockIcon from "@mui/icons-material/Lock";
import { tokens } from "../../theme";
import { StatusBadge } from "../../components/states";
import { formatRelativeTime } from "../../utils/dateUtils";
import { resolveSiteGeo } from "../../data/siteGeo";

// Maps site.status -> StatusBadge variant + label
const STATUS_META = {
  online:      { label: "Online",      variant: "success"  },
  offline:     { label: "Offline",     variant: "neutral"  },
  maintenance: { label: "Maintenance", variant: "warning"  },
  unknown:     { label: "Unknown",     variant: "neutral"  },
};

const TASK_LABEL = {
  tabular: "Tabular",
  imaging_classification: "Imaging · Classification",
  imaging_segmentation: "Imaging · Segmentation",
};

/**
 * Read-only summary card for a single ClientSite.
 *
 * Visual treatment:
 *  - online           → full color, no overlay
 *  - offline / maint. → muted opacity + colored status dot
 *  - access_scope === "denied" → "Access required" overlay + Lock icon
 *
 * Cards are NOT selectable here — selection happens inside the configs scene
 * via <SiteSelectionPanel>. This page is a read-only directory.
 */
const SiteCard = ({ site, capabilities = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const statusMeta = STATUS_META[site.status] || STATUS_META.unknown;
  const denied = site.access_scope === "denied";
  const muted = site.status !== "online" || denied;

  // City for the location row (pin icon). Resolved from the site geo table by
  // site_id / organization_id; presentation only.
  const city = resolveSiteGeo(site)?.city;

  return (
    <Card
      sx={{
        height: "100%",
        opacity: muted ? 0.75 : 1,
        position: "relative",
        border: "1px solid var(--fx-border)",
        borderRadius: 2,
        boxShadow: "var(--fx-shadow-sm)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: "var(--fx-border-strong)",
          boxShadow: "var(--fx-shadow-lg)",
        },
      }}
    >
      <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1,
                backgroundColor: "rgba(0,135,180,0.10)",
                color: colors.secondary[500],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HubIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 600, lineHeight: 1.2, fontSize: "0.9375rem" }}>
              {site.display_name}
            </Typography>
          </Stack>
          <Tooltip title={`status: ${site.status}`} placement="top">
            <span>
              <StatusBadge variant={statusMeta.variant} label={statusMeta.label} dot />
            </span>
          </Tooltip>
        </Stack>

        {(city || site.organization_display_name) && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <LocationOnIcon sx={{ fontSize: 16, color: colors.grey[500] }} />
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              {[city, site.organization_display_name].filter(Boolean).join(" · ")}
            </Typography>
          </Stack>
        )}

        <Box>
          <Typography variant="caption" sx={{ color: colors.grey[500], fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Supported tasks
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {(site.supported_tasks || []).map((t) => (
              <Chip
                key={t}
                size="small"
                label={TASK_LABEL[t] || t}
                sx={{
                  bgcolor: "var(--fx-inset)",
                  border: "1px solid var(--fx-border-strong)",
                  color: "var(--fx-text-primary)",
                  fontWeight: 500,
                  fontSize: "0.72rem",
                }}
              />
            ))}
            {(!site.supported_tasks || site.supported_tasks.length === 0) && (
              <Typography variant="body2" sx={{ color: colors.grey[500] }}>None</Typography>
            )}
          </Stack>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AccessTimeIcon sx={{ fontSize: 16, color: colors.grey[500] }} />
            <Typography variant="caption" sx={{ color: colors.grey[500] }}>
              Last seen {formatRelativeTime(site.last_seen) || "unknown"}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: colors.grey[500] }}>
            {capabilities.length} dataset{capabilities.length === 1 ? "" : "s"}
          </Typography>
        </Stack>

        {denied && (
          <Tooltip title="Access required - contact your administrator" placement="top">
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                bgcolor: "rgba(15, 23, 42, 0.85)",
                color: "#ffffff",
              }}
            >
              <LockIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ color: "#ffffff", fontWeight: 600 }}>
                Access required
              </Typography>
            </Box>
          </Tooltip>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteCard;
